import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  untracked,
  viewChild
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { MatError, MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';

import { TranslocoDirective } from '@jsverse/transloco';

import { BaseCustomerConfig, CustomerConfig } from '../../../../definitions/interfaces/customer/customer-config';
import { FirstErrorMessagePipe } from '../../../../pipes/first-error-message/first-error-message.pipe';
import { UserConfigsStoreFacade } from '../../../../stores/global-store/user-configs/user-configs-store.facade';
import { HelperService } from '../../../../utils/helper/helper.service';

@Component({
  selector: 'app-country-customer-selection',
  imports: [
    FirstErrorMessagePipe,
    FormsModule,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    MatOption,
    MatSelect,
    TranslocoDirective,
    ReactiveFormsModule
  ],
  providers: [
    HelperService
  ],
  templateUrl: './country-customer-selection.component.html',
  styleUrl: './country-customer-selection.component.sass',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CountryCustomerSelectionComponent {
  public disableCustomerInput = input.required<boolean>();
  public countryControl = input.required<FormControl>();
  public customerControl = input.required<FormControl>();
  public filteredCustomers = input.required<Array<BaseCustomerConfig>>();
  public selectedCustomer = input.required<BaseCustomerConfig | undefined>();
  public showHeader = input<boolean>(true);

  public readonly helper = inject(HelperService);
  public readonly userConfigsFacade = inject(UserConfigsStoreFacade);

  public customerAutocomplete = viewChild<MatAutocomplete>('customers');

  public countryList = this.userConfigsFacade.isSuperAdminSignal()
    ? this.userConfigsFacade.superAdminCountriesSignal()
    : [ this.userConfigsFacade.currentCountrySignal() ];

  public updateCustomerSelection = effect(() => {
    const selectedCustomer = this.selectedCustomer();
    untracked(() => {
      if (this.customerControl().value?.id !== selectedCustomer?.id) {
        this.customerControl().setValue(selectedCustomer);
        /* Automatically select the proper option in the autocomplete when the customerControl value is set programmatically
        * described here https://github.com/angular/components/issues/27861
        */
        if (this.customerAutocomplete() && selectedCustomer?.id) {
          const foundOption = this.customerAutocomplete()?.options?.find(
            (option) => option.value.id === selectedCustomer.id);
          if (!foundOption) return;
          setTimeout(() => foundOption.select(), 0);
        }
      }
    });
  });

  public disableInput = effect(() => {
    const isCustomerControlDisabled = this.disableCustomerInput();
    untracked(() => {
      if (isCustomerControlDisabled) {
        this.customerControl()?.disable();
      } else {
        this.customerControl()?.enable();
      }
    });
  });

  public customerErrors = {
    required: 'There are no configured customers for this country.',
    invalidCustomerValue: 'Invalid customer name',
  };

  public customerDisplayWith(customer: string | CustomerConfig): string {
    if (typeof customer === 'string') {
      return customer;
    }

    return customer?.name ?? '';
  }

  public onCountryChange(): void {
    this.customerControl().setValue(undefined);
    this.customerControl().markAsUntouched();
  }
}
